import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { CustomFilter, TextFilter } from 'components/Filtering';
import {
  Button, Icon, Modal, Tooltip
} from 'antd';
import {
  clearTextFilterSearchValue,
  getCustomFilters,
  getFieldFilters,
  getFilterIcon,
  getSortField,
  getTableState,
  getTextFilterInputChange,
  getTextFilterSearchValue,
  showFilterDropdown
} from 'library/helpers';
import settings from 'settings';
import _ from 'lodash';
import classNames from 'classnames';

const Confirm = Modal.confirm;
const DEFAULT_TABLE_PAGE_NO = settings.APP_CONFIG.defaultTablePage;
const DEFAULT_TABLE_PAGE_SIZE = settings.APP_CONFIG.defaultTablePageSize;

/**
 * TableWrapper
 * @description Higher Order Component for ListTable
 * @version 1.0.1
 */
const TableWrapper = (WrappedComponent) => {
  class HOC extends Component {
    constructor(props) {
      super(props);

      const {
        page, pageSize, sorter, filters, customFilters
      } = this.props;

      this.state = {
        page,
        pageSize,
        sorter,
        filters,
        customFilters,
        selectedRowKeys: []
      };

      this.loadList = this.loadList.bind(this);

      this.handleAddItemClick = this.handleAddItemClick.bind(this);
      this.handleDeleteItemClick = this.handleDeleteItemClick.bind(this);
      this.handleRefreshClick = this.handleRefreshClick.bind(this);

      this.handleTableChange = this.handleTableChange.bind(this);
      this.handleRowClick = this.handleRowClick.bind(this);
      this.handleRowSelectionChange = this.handleRowSelectionChange.bind(this);

      this.handleTextFilterDropdownVisibleChange = this.handleTextFilterDropdownVisibleChange.bind(this);
      this.handleTextFilterInputChange = this.handleTextFilterInputChange.bind(this);
      this.handleTextFilterSearch = this.handleTextFilterSearch.bind(this);
      this.handleTextFilterClearSearch = this.handleTextFilterClearSearch.bind(this);
      this.handleCustomFilters = this.handleCustomFilters.bind(this);

      this.clearSelectedRows = this.clearSelectedRows.bind(this);
    }

    componentDidMount() {
      const { onLoad } = this.props;
      this.loadList();
      onLoad(); // provide an onLoad event for parent
    }

    shouldComponentUpdate(nextProps) {
      const {
        filters, onLoad, selectedItemId, persistSelection
      } = this.props;
      // clear the selectedRows when item reset
      if (selectedItemId !== nextProps.selectedItemId && nextProps.selectedItemId === 0) {
        this.clearSelectedRows();
      }
      // reload list on initial update/add
      if (nextProps.isDirty && !nextProps.inFlight) {
        this.loadList(nextProps.filters);
        if (!persistSelection) this.clearSelectedRows();
      }
      // check if parent has changed
      if (!_.isEqual(nextProps.filters, filters)) {
        // use new filters to load related records
        this.loadList(nextProps.filters);
        if (!persistSelection) this.clearSelectedRows();
        onLoad(); // provide an onLoad event for parent change
      }

      return true;
    }

    /**
     * @description Assign filtering if filtering present in column
     * @param {column} Object
     * @return {object}
     */
    getTableFilter(column) {
      const { customFilters, filters } = this.state;
      const { defaultSettings } = this.props;
      if (filters[column.dataIndex]) {
        return {
          ...column,
          filteredValue: filters[column.dataIndex].length ? [filters[column.dataIndex]] : undefined
        };
      }
      if (!customFilters[column.dataIndex]) return {};
      const { filterSettings } = customFilters[column.dataIndex];
      return {
        filterIcon: getFilterIcon(customFilters[column.dataIndex].filtered),
        filterDropdownVisible: customFilters[column.dataIndex].filterDropdownVisible,
        filterDropdown: (
          <>
            {!filterSettings && (
              <TextFilter
                inputRef={(el) => {
                  this.filterSearchInput = el;
                }}
                placeholder={`Search ${column.title}`}
                value={customFilters[column.dataIndex].searchValue}
                onInputChange={value => this.handleTextFilterInputChange(column.dataIndex, value)}
                onOkClick={() => this.handleTextFilterSearch(column.dataIndex)}
                onResetClick={() => this.handleTextFilterClearSearch(column.dataIndex)}
              />
            )}
            {filterSettings && (
              <CustomFilter
                dataSource={defaultSettings}
                {...filterSettings}
                onUpdateCustomFilters={this.handleCustomFilters} // bring in the searchValue
              />
            )}
          </>
        ),
        onFilterDropdownVisibleChange: visible => this.handleTextFilterDropdownVisibleChange(column.dataIndex, visible, this.filterSearchInput)
      };
    }

    /**
     * @description Merge filtering with column
     * @param {column} Object
     * @return {array}
     */
    getTableColumns() {
      const { columns, pinnedItems } = this.props;
      const {
        selectedItemId, showInlinePin, showInlineDelete, showInlineEdit, isRecordPinnable, isRecordEditable, isRecordDeletable
      } = this.props;
      const tableColumns = _.clone(columns);
      if (showInlinePin || showInlineDelete || showInlineEdit) {
        const showCount = [showInlinePin, showInlineDelete, showInlineEdit].filter(s => s).length;
        const columnWidth = (showCount === 3 || showCount === 2 ? '150px' : '40px');
        tableColumns.splice(0, 0, {
          title: '',
          dataIndex: 'id',
          width: columnWidth,
          align: 'center',
          render: (text, record) => (
            record.id === selectedItemId ? null
              : (
                <div className="bhp-item-commands">
                  {showInlinePin && isRecordPinnable(record) && (
                    <span className="thumbtack" onClick={() => this.handleRowPin(record)}>
                      <Icon type="pushpin" theme="filled" className={classNames({ 'is-pinned': pinnedItems.some(pi => pi.id === record.id) })} />
                    </span>
                  )}
                  {showInlineDelete && isRecordDeletable(record) && (
                    <Tooltip title="Delete" placement="left">
                      <Button type="primary" shape="circle" size="small" icon="delete" onClick={() => this.handleDeleteItemClick([record.id])} />
                    </Tooltip>
                  )}
                  {showInlineEdit && isRecordEditable(record) && (
                    <Tooltip title="Edit" placement="right">
                      <Button shape="circle" size="small" icon="edit" onClick={() => this.handleRowClick(record)} />
                    </Tooltip>
                  )}
                </div>
              )
          )
        });
      }
      return tableColumns.map(column => ({
        ...column,
        key: column.dataIndex,
        ...this.getTableFilter(column)
      }));
    }

    /**
     * @description Check to find item exsists in data first
     * @param {itemId} number
     * @triggers {deleteConfirm}
     */
    deleteCheck(itemId) {
      const { dataSource } = this.props;
      if (itemId >= 0) {
        const found = dataSource.find(item => item.id === itemId);
        if (found) {
          this.deleteConfirm(found);
        }
      }
    }

    /**
     * @description Popup a confirmation asking to delete or cancel
     * @param {item} Object
     */
    deleteConfirm(item) {
      const { confirmMessage, okText, cancelText } = this.props;

      const self = this;
      Confirm({
        title: 'Confirm Delete',
        content: confirmMessage(item),
        okText,
        cancelText,
        onOk() {
          self.deleteItem(item.id);
        },
        onCancel() {
        }
      });
    }

    /**
     * @description Trigger callback to parent to delete
     * and set the form state to false
     * @param {id} number
     * @triggers {onDeleteItem}
     */
    deleteItem(id) {
      const { onDeleteItem } = this.props;
      onDeleteItem(id);
    }

    /**
     * @description Trigger callback to parent to initiate a new item
     * @triggers {onAddItemClick}
     */
    handleAddItemClick() {
      const { onAddItemClick } = this.props;
      onAddItemClick(); // Invoke callback to the parent
    }

    /**
     * @description Handle the delete once delete Button is clicked
     * @param {keys} array
     * @triggers {deleteCheck}
     */
    handleDeleteItemClick(keys = []) {
      const { checkDelete } = this.props;
      const itemId = (keys.length === 1 ? keys[0] : -1);
      checkDelete ? this.deleteCheck(itemId) : this.deleteItem(itemId);
    }

    /**
     * @description Reload the list of results when refresh button clicked
     * @triggers {loadList}
     */
    handleRefreshClick() {
      this.loadList();
    }

    /**
     * @description Update table state when table is changed
     * @param {pagination} objectChanged
     * @param {filters} object
     * @param {sorter} object
     * @return {getTableState} object
     * @triggers {loadList}
     */
    handleTableChange(pagination, filters, sorter) {
      this.setState(prevState => getTableState(pagination.current, pagination.pageSize, { ...prevState.filters, ...filters }, sorter),
        () => {
          this.loadList();
        });
    }

    /**
     * @description Trigger callback to parent with Row Id
     * @triggers {onRowClick}
     */
    handleRowClick(row) {
      const { onRowClick, rowSelectionConfig } = this.props;
      const { selectedRowKeys } = this.state;
      if (!rowSelectionConfig.multiRow) {
        this.setState({
          selectedRowKeys: [row.id]
        }, () => onRowClick(row, selectedRowKeys)); // Invoke callback to the parent);
      }
    }

    /**
     * @description Adds or removes the row in `pinnedItems`
     */
    handleRowPin(row) {
      const { pinnedItems, onPinItem } = this.props;
      const isRowPinned = pinnedItems.some(pi => pi.id === row.id);
      let newPinnedItems = [];
      if (!isRowPinned) {
        // Item is not pinned. Go, pin it!
        newPinnedItems = [...pinnedItems, row];
      }
      else {
        // Item is pinned. Unpin it!
        newPinnedItems = pinnedItems.filter(item => item.id !== row.id);
      }
      onPinItem(newPinnedItems);
    }

    handleRowSelectionChange(selectedRowKeys, selectedRows) {
      const { onRowSelectionChange, persistSelection, dataSource } = this.props;
      if (persistSelection) {
        // Merge: keep previously selected keys not in current page, add/remove current page selections
        const currentPageIds = dataSource.map(row => row.id);
        const { selectedRowKeys: prevKeys } = this.state;
        const keptKeys = prevKeys.filter(k => !currentPageIds.includes(k));
        const newKeys = [...keptKeys, ...selectedRows.map(row => row.id)];
        this.setState({ selectedRowKeys: newKeys }, () => {
          onRowSelectionChange(newKeys, selectedRows);
        });
      }
      else {
        this.setState({ selectedRowKeys: selectedRows.map(row => row.id) }, () => {
          onRowSelectionChange(selectedRows.map(row => row.id), selectedRows);
        });
      }
    }

    /**
     * @description Update customFilters to reflect visible dropdown
     * @param {key} string
     * @param {visible} bool
     * @param {searchInput} object
     * @triggers {searchInput.focus()}
     */
    handleTextFilterDropdownVisibleChange(key, visible, searchInput) {
      this.setState(prevState => ({
        customFilters: showFilterDropdown(prevState.customFilters, key, visible)
      }), () => {
        searchInput && searchInput.focus();
      });
    }

    /**
     * @description Update customFilters to reflect filtering
     * @param {key} string
     * @param {value} string
     */
    handleTextFilterInputChange(key, text) {
      this.setState(prevState => ({
        customFilters: getTextFilterInputChange(prevState.customFilters, key, text)
      }));
    }

    /**
     * @description Update customFilters with Search Value and reset to first page
     * @param {key} string
     * @triggers {loadList}
     */
    handleTextFilterSearch(key) {
      this.setState(prevState => ({
        customFilters: getTextFilterSearchValue(prevState.customFilters, key),
        page: 1
      }), () => {
        this.loadList();
      });
    }

    /**
     * @description Clear customFilters
     * @triggers {loadList}
     */
    handleTextFilterClearSearch(key) {
      this.setState(prevState => ({
        customFilters: clearTextFilterSearchValue(prevState.customFilters, key),
        page: 1
      }), () => {
        this.loadList();
      });
    }

    /**
     * @description Combine customFilters
     * @triggers {loadList}
     */
    handleCustomFilters(customFilters) {
      this.setState(prevState => ({
        customFilters: {
          ...prevState.customFilters,
          ...customFilters
        },
        page: 1
      }), () => {
        this.loadList();
      });
    }

    /**
     * @description Update table state for filtering and pagination
     * @triggers {onLoadList}
     */
    loadList(nextFilters = {}) {
      const {
        filters: stateFilters, sorter: stateSorter, customFilters, page, pageSize
      } = this.state;
      const { onLoadList, filters, sorter } = this.props;
      const newFilters = { ...filters, ...stateFilters, ...nextFilters };
      const newSorter = { ...sorter, ...stateSorter };

      // update filters state to maintain existing filters
      this.setState({
        filters: newFilters,
        sorter: newSorter
      }, () => {
        // send current table state to API
        const sortBy = getSortField(newSorter);
        const fieldFilters = getFieldFilters(newFilters); // use new filters if provided
        const customFieldFilters = getCustomFilters(customFilters);
        const filterBy = customFieldFilters.concat(fieldFilters);
        onLoadList({
          page: page - 1,
          pageSize,
          filters: filterBy,
          sorted: sortBy
        });
      });
    }

    clearSelectedRows() {
      this.setState({
        selectedRowKeys: []
      });
    }

    render() {
      const {
        canAdmin, defaultSettings, filterSettings, footer, inFlight, rowSelectionConfig, selectedItemId, fitContents,
      } = this.props;
      const { page, pageSize, selectedRowKeys } = this.state;
      // default Table ToolBar button config
      const commands = {
        showAdd: canAdmin,
        showDelete: canAdmin,
        showRefresh: true
      };

      // current selected keys
      const keys = selectedItemId && selectedItemId > 0 ? [selectedItemId] : selectedRowKeys;
      // rowSelection keys and event callback
      const rowSelection = {
        selectedRowKeys,
        onChange: this.handleRowSelectionChange
      };
      // default Taable sizing for scrolliing
      const scroll = {
        x: 850,
        y: '100%'
      };
      // Base row selection of both permissions and config
      const allowRowSelection = canAdmin && rowSelectionConfig && rowSelectionConfig.multiRow;
      return (
        <div className={classNames({ 'bhp-container': !fitContents })}>
          <WrappedComponent
            commands={commands}
            loading={inFlight}
            rowKey="id"
            selectedItemId={selectedItemId}
            selectedRowKeys={keys}
            scroll={scroll}
            onCommandAddClick={this.handleAddItemClick}
            onCommandDeleteClick={this.handleDeleteItemClick}
            onCommandRefreshClick={this.handleRefreshClick}
            onTableChange={this.handleTableChange}
            onRowClick={this.handleRowClick}
            {...this.props}
            page={page}
            pageSize={pageSize}
            columns={this.getTableColumns()}
            rowSelection={allowRowSelection ? rowSelection : null}
            customFilter={(
              <CustomFilter
                dataSource={defaultSettings}
                {...filterSettings}
                onUpdateCustomFilters={this.handleCustomFilters}
              />
            )}
          />
          {footer}
        </div>
      );
    }
  }

  HOC.propTypes = {
    canAdmin: PropTypes.bool,
    cancelText: PropTypes.string,
    columns: PropTypes.array,
    confirmMessage: PropTypes.func,
    dataSource: PropTypes.array,
    defaultSettings: PropTypes.object,
    filterSettings: PropTypes.object,
    fitContents: PropTypes.bool,
    footer: PropTypes.object,
    inFlight: PropTypes.bool,
    isDirty: PropTypes.bool,
    okText: PropTypes.string,
    onAddItemClick: PropTypes.func,
    onDeleteItem: PropTypes.func,
    onLoadList: PropTypes.func,
    onPinItem: PropTypes.func,
    onRowClick: PropTypes.func,
    onRowSelectionChange: PropTypes.func,
    persistSelection: PropTypes.bool,
    onLoad: PropTypes.func,
    page: PropTypes.number,
    pageSize: PropTypes.number,
    pinnedItems: PropTypes.array,
    showInlinePin: PropTypes.bool,
    showInlineEdit: PropTypes.bool,
    showInlineDelete: PropTypes.bool,
    isRecordPinnable: PropTypes.func,
    isRecordEditable: PropTypes.func,
    isRecordDeletable: PropTypes.func,
    sorter: PropTypes.object,
    filters: PropTypes.object,
    customFilters: PropTypes.object,
    selectedItemId: PropTypes.number,
    rowSelectionConfig: PropTypes.object,
    checkDelete: PropTypes.bool
  };

  HOC.defaultProps = {
    canAdmin: false,
    cancelText: 'Cancel',
    columns: [],
    confirmMessage: () => (
      <div>
        <p>
          Are you sure you wish to delete this record?
        </p>
        <p>
          This action cannot be undone.
        </p>
      </div>
    ),
    dataSource: [],
    defaultSettings: {},
    filterSettings: {},
    fitContents: false,
    footer: null,
    inFlight: false,
    isDirty: false,
    okText: 'Delete',
    onAddItemClick() {
    },
    onDeleteItem() {
    },
    onPinItem() {
    },
    onLoadList() {
    },
    onRowClick() {
    },
    onRowSelectionChange() {
    },
    persistSelection: false,
    onLoad() {
    },
    page: DEFAULT_TABLE_PAGE_NO,
    pageSize: DEFAULT_TABLE_PAGE_SIZE,
    pinnedItems: [],
    showInlinePin: false,
    showInlineEdit: false,
    showInlineDelete: false,
    isRecordPinnable: () => false,
    isRecordEditable: () => true,
    isRecordDeletable: () => true,
    sorter: {},
    filters: {},
    customFilters: {},
    selectedItemId: 0,
    rowSelectionConfig: {},
    checkDelete: true
  };

  return HOC;
};

export default TableWrapper;
